import { useState, useEffect, useCallback, useRef } from 'react';
import { safeStorage } from './lib/safeStorage';
import { PLANS } from './config/plans';
import { InputScreen } from './components/InputScreen';
import { LoadingScreen } from './components/LoadingScreen';
import { ResultsScreen } from './components/ResultsScreen';
import { SavedTripsScreen } from './components/SavedTripsScreen';
import { SettingsModal, DEFAULT_DISTANCE_SETTINGS, DEFAULT_CATEGORY_SETTINGS } from './components/SettingsModal';
import { AuthModal, type AuthDismissReason } from './components/AuthModal';
import { PaymentModal } from './components/PaymentModal';
import { ProScreen } from './components/ProScreen';
import { usePlaces, type FindMoreStage, type SearchProgressCallback, type Place } from './hooks/usePlaces';
import { useLLM } from './hooks/useLLM';
import { useAuth } from './hooks/useAuth';
import { MAX_FREE_DAY_GROUPS, MAX_FREE_LOCATION_GROUPS, saveReevaluatedTrip, type SavedItinerary } from './hooks/useItineraries';
import { useSettings, type TripGrouping } from './hooks/useSettings';
import { useSubscription, isPremiumActive, type SubscriptionType } from './hooks/useSubscription';
import { useLanguage } from './hooks/useLanguage';
import { useTheme } from './hooks/useTheme';
import type { Language } from './translations';
import type { RankedPlace } from './hooks/useLLM';
import type { AiMode } from './hooks/useSettings';
import { balanceByVibes } from './utils/balanceVibes';
import { applyTasteRerank, buildTasteHintText } from './utils/applyTasteRerank';
import { useTasteWeights } from './hooks/useTasteWeights';
import { BottomTabBar } from './components/BottomTabBar';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { SharedTripPage } from './pages/SharedTripPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import AdminPage from './pages/AdminPage';
import { VersionUpdateDialog } from './components/VersionUpdateDialog';
import { supabase } from './lib/supabase';
import { useSearchCredits, canUnregisteredUseInterest, incrementUnregisteredInterest, decrementUnregisteredInterest, captureReferrer, getStoredReferrer, clearStoredReferrer } from './hooks/useSearchCredits';

// Paddle is loaded by an external <script> tag (see index.html). Augment Window
// so `window.Paddle` is typed at the call sites without `any`.
interface PaddleApi {
  Initialize: (config: { token: string }) => void;
  Checkout: {
    open: (config: {
      items: { priceId: string; quantity: number }[];
      customer?: { email?: string };
      customData?: Record<string, string>;
      settings?: {
        successUrl?: string;
        displayMode?: 'overlay' | 'inline';
        theme?: 'light' | 'dark';
        locale?: string;
      };
    }) => void;
  };
}
declare global {
  interface Window {
    Paddle?: PaddleApi;
  }
}
import { useSavedInterests } from './hooks/useSavedInterests';
import { useIsAdmin } from './hooks/useIsAdmin';
import { fetchWeather } from './hooks/useWeather';
import { buildWeatherLine } from './utils/weatherPrompt';
import { reportError } from './lib/reportError';
import { useSessionReady } from './hooks/useSessionReady';
import { readPendingSave, writePendingSave, clearPendingSave } from './lib/pendingSaveStore';
import { readPendingReevaluate, clearPendingReevaluate } from './lib/pendingReevaluateStore';
import { TripsProvider, useTripsContext } from './contexts/TripsContext';
import { PlanningProvider, usePlanningContext, type SearchData } from './contexts/PlanningContext';
import { UiFeedbackProvider, useUiFeedbackContext } from './contexts/UiFeedbackContext';

// URL routing — stable, evaluated once before React renders
const sharedTripSlug = (() => {
  const match = window.location.pathname.match(/^\/t\/([a-z0-9-]+)$/);
  return match ? match[1] : null;
})();
const isResetPasswordPage =
  window.location.pathname === '/reset-password' ||
  window.location.hash.includes('type=recovery');
const isAdminPage = window.location.pathname === '/admin';

const SIGNED_OUT_DIRTY_KEY = 'tb_settings_dirty';
const SESSION_ONLY_TOAST_KEY = 'tb_settings_session_only_shown';

function readSignedOutDirtySet(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SIGNED_OUT_DIRTY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSignedOutDirtySet(dirty: Set<string>): void {
  try {
    if (dirty.size === 0) sessionStorage.removeItem(SIGNED_OUT_DIRTY_KEY);
    else sessionStorage.setItem(SIGNED_OUT_DIRTY_KEY, JSON.stringify([...dirty]));
  } catch {
    // sessionStorage unavailable: the in-memory set still covers same-page sign-in
  }
}
captureReferrer();

type AppMode = 'planning' | 'savedTrips' | 'settings' | 'filters' | 'pro';

interface PaymentState {
  isOpen: boolean;
  dayGroups: number;
  locationGroups: number;
  pendingPlaces: RankedPlace[] | null;
  /** Which save opened the paywall: a single card tap keeps the results-page trip state in step. */
  source?: 'instant' | 'selection';
}


// ═══════════════════════════════════════════════════════════════════════════
// Top-level — URL routing
// ═══════════════════════════════════════════════════════════════════════════
function App() {
  let route;
  if (sharedTripSlug) route = <SharedTripPage slug={sharedTripSlug} />;
  else if (isResetPasswordPage) route = <ResetPasswordPage />;
  else if (isAdminPage) route = <AdminPage />;
  else route = <AppMain />;
  // VersionUpdateDialog mounts on every route — surfaces a "new version
  // available" modal when the deployed bundle has been updated. See
  // src/components/VersionUpdateDialog.tsx + buildIdPlugin in vite.config.ts.
  return (
    <>
      {route}
      <VersionUpdateDialog />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Provider shell — owns only state needed as provider props
// ═══════════════════════════════════════════════════════════════════════════
function AppMain() {
  const [appMode, setAppMode] = useState<AppMode>('planning');
  const [tripGrouping, setTripGrouping] = useState<TripGrouping>('day');
  const [referralCode, setReferralCode] = useState<string | null>(null);

  const initialReferralBanner = !!getStoredReferrer() && safeStorage.getItem('tb_ref_dismissed') !== '1';

  return (
    <UiFeedbackProvider initialReferralBanner={initialReferralBanner}>
      <PlanningProvider>
        <TripsProvider
          tripGrouping={tripGrouping}
          referralCode={referralCode}
        >
          <AppContent
            appMode={appMode}
            setAppMode={setAppMode}
            tripGrouping={tripGrouping}
            setTripGrouping={setTripGrouping}
            referralCode={referralCode}
            setReferralCode={setReferralCode}
          />
        </TripsProvider>
      </PlanningProvider>
    </UiFeedbackProvider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main app content — consumes all three contexts
// ═══════════════════════════════════════════════════════════════════════════
interface AppContentProps {
  appMode: AppMode;
  setAppMode: (mode: AppMode) => void;
  tripGrouping: TripGrouping;
  setTripGrouping: (g: TripGrouping) => void;
  referralCode: string | null;
  setReferralCode: (code: string | null) => void;
}

function AppContent({
  appMode,
  setAppMode,
  tripGrouping,
  setTripGrouping,
  referralCode,
  setReferralCode,
}: AppContentProps) {
  const trips = useTripsContext();
  const planning = usePlanningContext();
  const feedback = useUiFeedbackContext();

  // ── Remaining local state ──
  const [paymentState, setPaymentState] = useState<PaymentState>({
    isOpen: false, dayGroups: 0, locationGroups: 0, pendingPlaces: null,
  });
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>('free');
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string | null>(null);
  const [subscriptionPausedUntil, setSubscriptionPausedUntil] = useState<string | null>(null);
  // Derived premium state. Snapshot logic mirrors tripboom-website Trips.tsx:
  // paid type AND (not expired OR currently paused), unless the pause started
  // after the paid period already expired. Replaces a previous naive
  // `subscriptionType !== 'free'` check that silently treated any non-free
  // type as Premium regardless of expiry or pause.
  const isPremium = isPremiumActive(subscriptionType, subscriptionExpiresAt, subscriptionPausedUntil);
  const [isBetaMode, setIsBetaMode] = useState(true);

  // ── Hooks ──
  const { searchNearby, validateLocation, loadMoreByStage, performCategoryTopupForRanking, loadingMore } = usePlaces();
  const { rankPlaces } = useLLM();
  const { user, isAuthenticated, signIn, signUp, signInWithGoogle, signInWithEmailOtp, verifyEmailOtp, resetPassword, signOut } = useAuth();
  const { ready: sessionReady, error: sessionReadyError } = useSessionReady(user?.id ?? null);
  const { weights: tasteWeights } = useTasteWeights(user?.id ?? null);
  const { language, setLanguage, t } = useLanguage();
  const { themeMode, setThemeMode } = useTheme();
  const { loadSettings, debouncedSave } = useSettings();
  const { wouldExceedLimit, upgradeSubscription, cancelSubscription, pauseSubscription, resumeSubscription, getSubscription } = useSubscription();
  const { credits: searchCredits, loadCredits, clearCredits, consumeInterestSearch, refundInterestSearch, consumeDeepSearch, claimFirstSaveBonus, claimReferralReward } = useSearchCredits();
  const { interests: savedInterests, useInterests: useSavedInterestsActive, hasAny: hasSavedInterests, loadInterests: loadSavedInterests, toggleUseInterests, autoSaveInterest, getActiveInterests, addInterest: addSavedInterest, removeInterest: removeSavedInterest, clearInterests } = useSavedInterests();
  const { isAdmin } = useIsAdmin();

  // ── Refs ──
  const settingsLoadedRef = useRef(false);
  const settingsLoadingRef = useRef(false);
  const pendingSaveDataRef = useRef<{ searchData: SearchData; places: RankedPlace[] } | null>(null);
  const pendingUpgradeRef = useRef<'trip_pass' | 'monthly' | 'yearly' | null>(null);
  const pendingSaveHandledRef = useRef(false);
  // H-215: tracks whether we've already drained the pendingReevaluateStore
  // for this auth session, so the materialiser doesn't double-fire.
  const pendingReevaluateHandledRef = useRef(false);
  // Settings changed while signed out must survive a sign-in that leaves the
  // page (Google OAuth redirects), so the dirty set lives in sessionStorage
  // for the life of the tab, not only in memory (#96).
  const sessionDirtyRef = useRef<Set<string>>(readSignedOutDirtySet());
  const initialThemeRef = useRef(themeMode);
  const initialLanguageRef = useRef(language);
  const initialDistanceRef = useRef(planning.distanceSettings);
  const initialCategoryRef = useRef(planning.categorySettings);
  const initialShowOpenRef = useRef(planning.showOpenOnly);
  const initialMinRatingRef = useRef(planning.minRating);
  const initialAiModeRef = useRef(planning.aiMode);

  // ── Paddle init ──
  useEffect(() => {
    if (typeof window !== 'undefined' && window.Paddle) {
      window.Paddle.Initialize({ token: 'live_40b8c07da0001b43b7c8a01cbc2' });
    }
  }, []);

  // ── Settings dirty tracking ──
  useEffect(() => {
    if (!isAuthenticated && !settingsLoadedRef.current) {
      if (themeMode !== initialThemeRef.current) sessionDirtyRef.current.add('theme_mode');
      if (language !== initialLanguageRef.current) sessionDirtyRef.current.add('language');
      if (planning.distanceSettings !== initialDistanceRef.current) sessionDirtyRef.current.add('distance_settings');
      if (planning.categorySettings !== initialCategoryRef.current) sessionDirtyRef.current.add('category_settings');
      if (planning.showOpenOnly !== initialShowOpenRef.current) sessionDirtyRef.current.add('show_open_only');
      if (planning.minRating !== initialMinRatingRef.current) sessionDirtyRef.current.add('min_rating');
      if (planning.aiMode !== initialAiModeRef.current) sessionDirtyRef.current.add('ai_mode');
      writeSignedOutDirtySet(sessionDirtyRef.current);
    }
  }, [isAuthenticated, themeMode, language, planning.distanceSettings, planning.categorySettings, planning.showOpenOnly, planning.minRating, planning.aiMode]);

  // Signed out, filters live in memory and reset on refresh (theme and language
  // are kept on the device). Say so once per tab, the first time a filter
  // actually changes while signed out, never on the sign-out transition itself.
  const filterSnapshot = JSON.stringify([planning.distanceSettings, planning.categorySettings, planning.showOpenOnly, planning.minRating, planning.aiMode]);
  const prevFilterStateRef = useRef<{ snapshot: string; signedIn: boolean } | null>(null);
  useEffect(() => {
    const prev = prevFilterStateRef.current;
    prevFilterStateRef.current = { snapshot: filterSnapshot, signedIn: isAuthenticated };
    if (isAuthenticated || !prev || prev.signedIn || prev.snapshot === filterSnapshot) return;
    if (sessionStorage.getItem(SESSION_ONLY_TOAST_KEY) === '1') return;
    try { sessionStorage.setItem(SESSION_ONLY_TOAST_KEY, '1'); } catch { /* sessionStorage unavailable: the toast may repeat */ }
    feedback.showCreditToast(t.settings.settingsSessionOnly, 8000, { label: t.common.signIn, onClick: () => feedback.setAuthOpen(true) });
  }, [filterSnapshot, isAuthenticated, feedback, t]);

  // ── First-save bonus helper ──
  const maybeClaimFirstSaveBonus = useCallback(() => {
    if (user?.id && searchCredits && !searchCredits.firstSaveBonusClaimed) {
      claimFirstSaveBonus(user.id).then(claimed => {
        if (claimed) feedback.showCreditToast(t.toasts.firstSaveBonus);
      });
    }
  }, [user?.id, searchCredits, claimFirstSaveBonus, t, feedback]);

  const triggerTripSaved = useCallback(() => {
    trips.startTripSavedHighlight();
    maybeClaimFirstSaveBonus();
  }, [trips, maybeClaimFirstSaveBonus]);

  // ── Auth init + settings load ──
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const userId = user.id;
      trips.loadSavedItineraries();

      getSubscription(userId).then((sub) => {
        setSubscriptionType(sub.type);
        setSubscriptionExpiresAt(sub.expiresAt);
        setSubscriptionPausedUntil(sub.pausedUntil);
        if (sub.justExpired) feedback.showToast('expired');
      });

      loadCredits(userId);
      loadSavedInterests(userId);

      if (supabase) {
        Promise.all([
          supabase.from('app_config').select('value').eq('key', 'BETA_MODE').maybeSingle(),
          supabase.from('user_settings').select('is_beta_user').eq('user_id', userId).maybeSingle(),
        ]).then(([{ data: config }, { data: userRow }]) => {
          setIsBetaMode((config?.value === true || config?.value === 'true') || userRow?.is_beta_user === true);
        });
      }

      const referrerId = getStoredReferrer();
      if (referrerId && referrerId !== userId) {
        feedback.setReferralBanner(false);
        claimReferralReward(userId, referrerId).then(reward => {
          clearStoredReferrer();
          safeStorage.remove('tb_ref_dismissed');
          if (reward) feedback.showCreditToast(t.toasts.referralReward.replace('{interest}', String(reward.interest)).replace('{deep}', String(reward.deep)));
        });
      } else {
        feedback.setReferralBanner(false);
      }

      if (!settingsLoadedRef.current && !settingsLoadingRef.current) {
        settingsLoadingRef.current = true;
        const dirty = sessionDirtyRef.current;
        const hadDirtySettings = dirty.size > 0;
        loadSettings(userId).then((saved) => {
          if (saved) {
            if (!dirty.has('distance_settings')) {
              const merged = { ...DEFAULT_DISTANCE_SETTINGS };
              if (saved.distance_settings && typeof saved.distance_settings === 'object') {
                for (const transport of ['walking', 'transit', 'taxi'] as const) {
                  if (saved.distance_settings[transport]) {
                    merged[transport] = { ...DEFAULT_DISTANCE_SETTINGS[transport], ...saved.distance_settings[transport] };
                  }
                }
              }
              planning.setDistanceSettings(merged);
            }
            if (!dirty.has('category_settings')) planning.setCategorySettings({ ...DEFAULT_CATEGORY_SETTINGS, ...saved.category_settings });
            if (!dirty.has('show_open_only')) planning.setShowOpenOnly(saved.show_open_only);
            if (!dirty.has('min_rating')) planning.setMinRating(saved.min_rating);
            if (saved.trip_grouping) setTripGrouping(saved.trip_grouping);
            if (saved.ai_mode && !dirty.has('ai_mode')) planning.setAiMode(saved.ai_mode);
            if (saved.theme_mode && !dirty.has('theme_mode')) setThemeMode(saved.theme_mode);
            if (saved.language && !dirty.has('language')) setLanguage(saved.language as Language);
            if (saved.referral_code) setReferralCode(saved.referral_code);
          }
          if (hadDirtySettings) {
            debouncedSave(userId, {
              distance_settings: planning.distanceSettings,
              category_settings: planning.categorySettings,
              show_open_only: planning.showOpenOnly,
              min_rating: planning.minRating,
              trip_grouping: tripGrouping,
              ai_mode: planning.aiMode,
              theme_mode: themeMode,
              language
            });
          }
          settingsLoadedRef.current = true;
          sessionDirtyRef.current = new Set();
          writeSignedOutDirtySet(sessionDirtyRef.current);
        });
      }
    } else {
      trips.resetTripsState();
      settingsLoadedRef.current = false;
      settingsLoadingRef.current = false;
      sessionDirtyRef.current = new Set();
      setSubscriptionType('free');
      setReferralCode(null);
      setSubscriptionExpiresAt(null);
      setSubscriptionPausedUntil(null);
      clearInterests();
      clearCredits();
      pendingSaveHandledRef.current = false;
      pendingReevaluateHandledRef.current = false;
    }
  }, [isAuthenticated, user?.id, trips.saveItinerary, trips.loadSavedItineraries, loadSettings, getSubscription]);

  // ── Pending-upgrade resume: "Activate" tapped while signed out ──
  useEffect(() => {
    if (!sessionReady || !isAuthenticated || !user?.id || !pendingUpgradeRef.current) return;
    const plan = pendingUpgradeRef.current;
    pendingUpgradeRef.current = null;
    void handleUpgrade(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, isAuthenticated, user?.id]);

  // ── Pending-save resume (H-103) ──
  useEffect(() => {
    if (!sessionReady || !isAuthenticated || !user?.id) return;
    if (pendingSaveHandledRef.current) return;

    if (sessionReadyError) {
      if (pendingSaveDataRef.current || readPendingSave()) {
        feedback.showErrorToast(t.toasts.saveFailed);
      }
      return;
    }

    const userId = user.id;
    const refData = pendingSaveDataRef.current;
    pendingSaveDataRef.current = null;
    const pendingData = refData ?? (refData ? null : readPendingSave());
    if (!pendingData) return;

    pendingSaveHandledRef.current = true;
    const { searchData: savedSearchData, places } = pendingData;
    const pendingVibes = savedSearchData.customVibe
      ? [...savedSearchData.vibes, `custom:${savedSearchData.customVibe}`]
      : savedSearchData.vibes;

    trips.saveItinerary(userId, savedSearchData.location, savedSearchData.timeAvailable, savedSearchData.transport, pendingVibes, places, planning.aiMode, savedSearchData.country)
      .then(async (saved) => {
        if (saved) {
          clearPendingSave();
          await trips.loadSavedItineraries();
          triggerTripSaved();
          trips.setSelectNewestTrip(true);
          setAppMode('savedTrips');
          return;
        }
        reportError('pendingSave.resumeReturnedNull', new Error('saveItinerary returned null'), { userId, placeCount: places.length, location: savedSearchData.location });
        feedback.showErrorToast(t.toasts.saveFailed);
        pendingSaveHandledRef.current = false;
      })
      .catch((err) => {
        reportError('pendingSave.resumeThrew', err, { userId, placeCount: places.length, location: savedSearchData.location });
        feedback.showErrorToast(t.toasts.saveFailed);
        pendingSaveHandledRef.current = false;
      });
  }, [sessionReady, sessionReadyError, isAuthenticated, user?.id, planning.aiMode, trips.saveItinerary, trips.loadSavedItineraries, t]);

  // ── H-215: Pending-reevaluate resume ──
  // Anonymous viewer reevaluated a shared trip and tapped "Save this trip",
  // which wrote the reevaluated payload to localStorage and sent them through
  // AuthModal. They sign in/up, land here, and we materialise a new owned
  // itinerary with conditional auto-pin (only if 0 existing pins).
  useEffect(() => {
    if (!sessionReady || !isAuthenticated || !user?.id) return;
    if (pendingReevaluateHandledRef.current) return;

    const pending = readPendingReevaluate();
    if (!pending) return;

    if (sessionReadyError) {
      feedback.showErrorToast(t.toasts.saveFailed);
      return;
    }

    pendingReevaluateHandledRef.current = true;
    const userId = user.id;

    saveReevaluatedTrip(userId, pending.shareSlug, pending.context, pending.reevaluated)
      .then(async (result) => {
        if (result) {
          clearPendingReevaluate();
          await trips.loadSavedItineraries();
          triggerTripSaved();
          trips.setSelectNewestTrip(true);
          setAppMode('savedTrips');
          feedback.showCreditToast(
            result.autoPinned
              ? (t.reevaluate?.toastSavedAndPinned || 'Trip saved and pinned to your plan!')
              : (t.reevaluate?.toastSaved || 'Trip saved! Pin it manually if you want it featured.'),
            4000,
          );
          return;
        }
        reportError('pendingReevaluate.resumeReturnedNull', new Error('saveReevaluatedTrip returned null'), { userId, placeCount: pending.reevaluated.length });
        feedback.showErrorToast(t.toasts.saveFailed);
        pendingReevaluateHandledRef.current = false;
      })
      .catch((err) => {
        reportError('pendingReevaluate.resumeThrew', err, { userId, placeCount: pending.reevaluated.length });
        feedback.showErrorToast(t.toasts.saveFailed);
        pendingReevaluateHandledRef.current = false;
      });
  }, [sessionReady, sessionReadyError, isAuthenticated, user?.id, trips.loadSavedItineraries, triggerTripSaved, trips, feedback, t]);

  // ── Settings persistence ──
  useEffect(() => {
    if (isAuthenticated && user?.id && settingsLoadedRef.current) {
      debouncedSave(user.id, {
        distance_settings: planning.distanceSettings,
        category_settings: planning.categorySettings,
        show_open_only: planning.showOpenOnly,
        min_rating: planning.minRating,
        trip_grouping: tripGrouping,
        ai_mode: planning.aiMode,
        theme_mode: themeMode,
        language,
      });
    }
  }, [isAuthenticated, user?.id, planning.distanceSettings, planning.categorySettings, planning.showOpenOnly, planning.minRating, tripGrouping, planning.aiMode, themeMode, language, debouncedSave]);

  useEffect(() => {
    if (isAuthenticated && user?.id && settingsLoadedRef.current) {
      trips.loadSavedItineraries(tripGrouping);
    }
  }, [tripGrouping]);

  // ── Taste hint builder ──
  const buildEnrichedTasteHint = (vibes: string[]): string | null => {
    let hint = buildTasteHintText(tasteWeights?.tasteHint ?? null);
    const activeInterests = getActiveInterests(vibes);
    if (activeInterests.length > 0) {
      const interestLine = `They have specifically saved these interests: ${activeInterests.join(', ')}. Prioritize places that match these.`;
      hint = hint ? `${hint} ${interestLine}` : interestLine;
    }
    return hint;
  };

  // ── Search handler ──
  /** AI High runs only while the user still holds a credit; otherwise Medium.
   *  The credit itself is taken afterwards by chargeHighCredit (#84). */
  const resolveAiMode = (): AiMode => {
    if (planning.aiMode !== 'high' || isPremium) return planning.aiMode;
    if (isAuthenticated && user?.id && (searchCredits?.deepSearchesRemaining ?? 0) > 0) return 'high';
    if (isAuthenticated) {
      planning.setAiMode('medium');
      feedback.showCreditToast(t.toasts.noAiHighCredits);
    }
    return 'medium';
  };

  /** Consume one AI High credit, and only when the AI actually served the
   *  ranking. Offline substitutes are free (#84, Ian 2026-09-26). */
  const chargeHighCredit = async (ranked: RankedPlace[], mode: AiMode) => {
    if (mode !== 'high' || isPremium || !user?.id) return;
    if (!ranked.some(p => p.source === 'ai')) return;
    const consumed = await consumeDeepSearch(user.id);
    if (!consumed) return;
    const freshRemaining = Math.max(0, (searchCredits?.deepSearchesRemaining ?? 1) - 1);
    feedback.showCreditToast(t.toasts.aiHighLeft.replace('{count}', String(freshRemaining)), 3000);
    if (freshRemaining <= 0) planning.setAiMode('medium');
  };

  const handleSubmit = async (data: SearchData, includeClosedPlaces: boolean = false) => {
    let chargedInterest: 'account' | 'device' | null = null;
    if (data.customVibe && !isPremium) {
      if (!isAuthenticated) {
        if (!canUnregisteredUseInterest()) {
          planning.setErrorMessage(t.errors.signInForInterest);
          return;
        }
        incrementUnregisteredInterest();
        chargedInterest = 'device';
      } else if (user?.id) {
        const consumed = await consumeInterestSearch(user.id);
        if (!consumed) { planning.setErrorMessage('CREDITS_EXHAUSTED'); return; }
        chargedInterest = 'account';
      }
    }

    // Any search that ends without results gives the interest credit back, exactly once.
    const refundInterestCredit = async () => {
      const charged = chargedInterest;
      chargedInterest = null;
      if (charged === 'device') { decrementUnregisteredInterest(); return; }
      if (charged === 'account' && user?.id) {
        const refunded = await refundInterestSearch(user.id);
        if (refunded) feedback.showCreditToast(t.toasts.interestCreditRefunded);
        else feedback.showErrorToast(t.toasts.refundFailed);
      }
    };

    const effectiveAiMode = resolveAiMode();

    planning.setErrorMessage(null);
    planning.setFilteredClosedCount(0);
    trips.setSearchLimitReached(false);
    trips.setCurrentResultsTripId(null);
    trips.setInstantSavedPlaceIds(new Set());
    trips.instantSavedPlacesRef.current = [];
    planning.setLoadingStage('validating');
    planning.setLoadingLocationName(data.location);
    planning.setLoadingPlacesFound(0);
    planning.setLoadingPlacesEliminated(0);
    planning.setShowTopupStage(false);
    planning.setFirstPassAnalyzed(0);
    planning.setFirstPassEliminated(0);
    planning.setRankingSubStage('sending');
    planning.setSearchSubStage('vibes');
    planning.setSearchProgress({ completed: 0, total: 0 });
    planning.setPlanningView('loading');

    const shouldIncludeClosed = includeClosedPlaces || !planning.showOpenOnly;

    try {
      let finalLocation = data.location;
      let searchCoords = data.coords;
      let searchCountry: string | undefined;

      if (!searchCoords) {
        const validation = await validateLocation(data.location);
        if (validation) {
          finalLocation = validation.correctedName;
          searchCoords = validation.coords;
          searchCountry = validation.country ?? undefined;
          planning.setLoadingLocationName(finalLocation);
        } else {
          await refundInterestCredit();
          planning.setSearchData({ ...data });
          planning.setErrorMessage(t.errors.locationNotFound);
          planning.setPlanningView('input');
          return;
        }
      }

      planning.setLoadingStage('searching');

      const handleSearchProgress: SearchProgressCallback = (stage) => {
        if (stage === 'topup') { planning.setShowTopupStage(true); planning.setLoadingStage('topup'); }
        else planning.setLoadingStage(stage);
      };

      const result = await searchNearby(finalLocation, data.vibes, searchCoords, data.transport, data.timeAvailable, planning.distanceSettings, planning.categorySettings, shouldIncludeClosed, planning.minRating, handleSearchProgress, language, data.customVibe);

      if (result.aborted) { await refundInterestCredit(); return; }

      if (result.failed) {
        await refundInterestCredit();
        planning.setSearchData({ ...data, location: finalLocation, coords: searchCoords, country: searchCountry });
        planning.setErrorMessage(t.errors.searchUnavailable);
        planning.setPlanningView('input');
        return;
      }

      if (result.correctedLocation) { finalLocation = result.correctedLocation; planning.setLoadingLocationName(finalLocation); }

      if (result.customVibeDegraded) await refundInterestCredit();

      planning.setSearchData({ ...data, location: finalLocation, coords: searchCoords, country: searchCountry });
      planning.setFilteredClosedCount(result.filteredClosedCount);

      if (result.places.length === 0) {
        await refundInterestCredit();
        planning.setErrorMessage(result.filteredClosedCount > 0
          ? t.errors.noOpenPlacesFound.replace('{count}', String(result.filteredClosedCount))
          : t.errors.noPlacesFound);
        planning.setPlanningView('input');
        return;
      }

      if (user?.id && supabase) supabase.rpc('increment_search_counter', { p_user_id: user.id }).then(() => {});

      const effectiveVibes = data.customVibe ? [...data.vibes, `custom:${data.customVibe}`] : data.vibes;

      if (data.customVibe && user?.id) {
        autoSaveInterest(user.id, data.customVibe, data.vibes.length === 1 ? data.vibes[0] : null, true);
      }

      const RAW_TOPUP_THRESHOLD = 20;
      const shouldTopup = effectiveAiMode === 'high' || (effectiveAiMode === 'medium' && result.places.length < RAW_TOPUP_THRESHOLD);
      let allCandidates = result.places;

      if (shouldTopup) {
        console.log(`Raw places: ${result.places.length}. Running topup before ranking (mode: ${effectiveAiMode})...`);
        planning.setShowTopupStage(true);
        planning.setLoadingStage('topup');
        // The top-up only widens the pool; if it fails, rank what the main search found.
        const topupPlaces = await performCategoryTopupForRanking(result.places.length, effectiveAiMode)
          .catch((err: unknown) => { console.warn('Top-up search failed; ranking the main results only:', err); return [] as Place[]; });
        if (topupPlaces.length > 0) {
          console.log(`Topup found ${topupPlaces.length} additional places.`);
          const seenIds = new Set(result.places.map(p => p.id));
          allCandidates = [...result.places, ...topupPlaces.filter((p: Place) => !seenIds.has(p.id))];
        }
      }

      planning.setLoadingPlacesFound(allCandidates.length);
      planning.setLoadingStage('ranking');
      planning.setRankingSubStage('sending');

      const searchWeather = searchCoords ? await fetchWeather(searchCoords.lat, searchCoords.lng) : null;
      const weatherLine = planning.considerWeather ? buildWeatherLine(searchWeather, data.timeAvailable, data.transport) : null;

      const ranked = await rankPlaces(allCandidates, finalLocation, data.timeAvailable, data.transport, effectiveVibes, language, effectiveAiMode, buildEnrichedTasteHint(effectiveVibes), weatherLine);
      await chargeHighCredit(ranked, effectiveAiMode);

      if (ranked.length === 0) { await refundInterestCredit(); planning.setErrorMessage(t.errors.unableToRank); planning.setPlanningView('input'); return; }

      planning.setRankingSubStage('analysing');
      planning.setFirstPassAnalyzed(allCandidates.length);
      planning.setFirstPassEliminated(Math.max(0, allCandidates.length - ranked.length));
      planning.setLoadingPlacesEliminated(Math.max(0, allCandidates.length - ranked.length));

      await new Promise(r => requestAnimationFrame(() => r(undefined)));
      planning.setLoadingStage('complete');

      const balanced = balanceByVibes(ranked, effectiveVibes);
      const tasteRanked = applyTasteRerank(balanced, tasteWeights);
      planning.setRankedPlaces(tasteRanked);

      await new Promise(resolve => setTimeout(resolve, 300));
      planning.setPlanningView('results');
    } catch (error) {
      console.error('Search failed:', error);
      await refundInterestCredit();
      planning.setSearchData({ ...data });
      planning.setErrorMessage(t.errors.searchUnavailable);
      planning.setPlanningView('input');
    }
  };

  const handleBack = () => {
    planning.setPlanningView('input');
    planning.setRankedPlaces([]);
    planning.setErrorMessage(null);
    trips.setFindMoreStage(0);
    trips.setCurrentResultsTripId(null);
    trips.setInstantSavedPlaceIds(new Set());
    trips.instantSavedPlacesRef.current = [];
  };

  const handleSaveItinerary = async (selectedPlaces: RankedPlace[]): Promise<SavedItinerary | null> => {
    if (!planning.searchData) return null;
    if (!isAuthenticated || !user?.id) {
      pendingSaveDataRef.current = { searchData: planning.searchData, places: selectedPlaces };
      writePendingSave(planning.searchData, selectedPlaces);
      pendingSaveHandledRef.current = false;
      feedback.setAuthOpen(true);
      return null;
    }
    const { exceeds, dayGroups, locationGroups } = await wouldExceedLimit(user.id, planning.searchData.location, MAX_FREE_DAY_GROUPS, MAX_FREE_LOCATION_GROUPS);
    if (exceeds) { setPaymentState({ isOpen: true, dayGroups, locationGroups, pendingPlaces: selectedPlaces, source: 'selection' }); return null; }

    const effectiveVibes = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
    const saved = await trips.saveItinerary(user.id, planning.searchData.location, planning.searchData.timeAvailable, planning.searchData.transport, effectiveVibes, selectedPlaces, planning.aiMode, planning.searchData.country);
    if (saved) { await trips.loadSavedItineraries(); triggerTripSaved(); return saved; }
    return null;
  };

  const handleInstantSave = async (place: RankedPlace): Promise<string | null> => {
    if (!planning.searchData) return null;
    if (!isAuthenticated || !user?.id) {
      pendingSaveDataRef.current = { searchData: planning.searchData, places: [place] };
      writePendingSave(planning.searchData, [place]);
      pendingSaveHandledRef.current = false;
      feedback.setAuthOpen(true);
      return null;
    }
    try {
      if (trips.currentResultsTripId) {
        const success = await trips.addPlacesToItinerary(trips.currentResultsTripId, [place]);
        if (success) {
          trips.instantSavedPlacesRef.current = [...trips.instantSavedPlacesRef.current, place];
          if (trips.instantSavedPlacesRef.current.length === 2) {
            const regenVibes = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
            await trips.regenerateTripTitle(trips.currentResultsTripId, planning.searchData.location, regenVibes, trips.instantSavedPlacesRef.current, planning.searchData.timeAvailable, planning.searchData.transport);
          }
          await trips.loadSavedItineraries();
          triggerTripSaved();
          return trips.currentResultsTripId;
        }
        return null;
      }
      const { exceeds, dayGroups, locationGroups } = await wouldExceedLimit(user.id, planning.searchData.location, MAX_FREE_DAY_GROUPS, MAX_FREE_LOCATION_GROUPS);
      if (exceeds) { setPaymentState({ isOpen: true, dayGroups, locationGroups, pendingPlaces: [place], source: 'instant' }); return null; }

      const effectiveVibes2 = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
      const saved = await trips.saveItinerary(user.id, planning.searchData.location, planning.searchData.timeAvailable, planning.searchData.transport, effectiveVibes2, [place], planning.aiMode, planning.searchData.country);
      if (saved) {
        trips.setCurrentResultsTripId(saved.id);
        trips.instantSavedPlacesRef.current = [place];
        await trips.loadSavedItineraries();
        triggerTripSaved();
        return saved.id;
      }
      return null;
    } catch (error) { console.error('Instant save failed:', error); return null; }
  };

  const handleInstantRemove = async (placeId: string): Promise<boolean> => {
    if (!trips.currentResultsTripId || !planning.searchData) return false;
    try {
      const success = await trips.deleteSavedPlaceByPlaceId(trips.currentResultsTripId, placeId);
      if (success) {
        trips.instantSavedPlacesRef.current = trips.instantSavedPlacesRef.current.filter(p => p.id !== placeId);
        if (trips.instantSavedPlacesRef.current.length === 1) {
          const regenVibes2 = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
          await trips.regenerateTripTitle(trips.currentResultsTripId, planning.searchData.location, regenVibes2, trips.instantSavedPlacesRef.current, planning.searchData.timeAvailable, planning.searchData.transport);
        }
        await trips.loadSavedItineraries();
        return true;
      }
      return false;
    } catch (error) { console.error('Instant remove failed:', error); return false; }
  };

  const handleUpgrade = async (plan: 'trip_pass' | 'monthly' | 'yearly'): Promise<boolean> => {
    if (!supabase) return false;
    if (!user?.id) {
      // Signed out: sign in first, then carry on with the same plan.
      pendingUpgradeRef.current = plan;
      feedback.setAuthOpen(true);
      return false;
    }
    const { data: betaConfig } = await supabase.from('app_config').select('value').eq('key', 'BETA_MODE').maybeSingle();
    const { data: userSettings } = await supabase.from('user_settings').select('is_beta_user').eq('user_id', user.id).maybeSingle();
    if ((betaConfig?.value === true || betaConfig?.value === 'true') || userSettings?.is_beta_user === true) {
      const success = await upgradeSubscription(user.id, plan);
      if (success) {
        setSubscriptionType(plan);
        const sub = await getSubscription(user.id);
        setSubscriptionExpiresAt(sub.expiresAt);
        if (paymentState.pendingPlaces && planning.searchData) {
          const paymentVibes = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
          const pendingPlaces = paymentState.pendingPlaces;
          const saved = await trips.saveItinerary(user.id, planning.searchData.location, planning.searchData.timeAvailable, planning.searchData.transport, paymentVibes, pendingPlaces, planning.aiMode, planning.searchData.country);
          if (saved) {
            if (paymentState.source === 'instant') {
              trips.setCurrentResultsTripId(saved.id);
              trips.instantSavedPlacesRef.current = [...pendingPlaces];
              trips.setInstantSavedPlaceIds(new Set(pendingPlaces.map(p => p.id)));
            }
            await trips.loadSavedItineraries();
            triggerTripSaved();
          }
        }
      }
      return success;
    }
    const priceId = PLANS[plan].paddlePriceId;
    if (!priceId || !window.Paddle) return false;
    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: { email: user.email },
      customData: { user_id: user.id },
      settings: { successUrl: window.location.href, displayMode: 'overlay', theme: 'dark', locale: language || 'en' },
    });
    return true;
  };

  const handleOpenPaymentFromSettings = () => {
    feedback.setSettingsOpen(false);
    setPaymentState({ isOpen: true, dayGroups: 0, locationGroups: 0, pendingPlaces: null });
  };

  const handleCancelPremium = async () => {
    if (!user?.id) return;
    const success = await cancelSubscription(user.id);
    if (success) {
      setSubscriptionType('free');
      setSubscriptionPausedUntil(null);
      feedback.showCreditToast(t.toasts.cancelDone);
    } else {
      feedback.showErrorToast(t.toasts.cancelFailed);
    }
  };

  const handlePauseSubscription = async (months: 1 | 2 | 3 | 'indefinite') => {
    if (!user?.id) return;
    const success = await pauseSubscription(user.id, months);
    if (success) {
      const sub = await getSubscription(user.id);
      setSubscriptionPausedUntil(sub.pausedUntil);
      setSubscriptionExpiresAt(sub.expiresAt);
      feedback.showCreditToast(months === 'indefinite'
        ? 'Subscription paused indefinitely. Pro stays active until your paid period ends.'
        : `Paused for ${months} month${months > 1 ? 's' : ''}. Pro stays active until your paid period ends.`);
    } else {
      feedback.showErrorToast(t.toasts.pauseFailed);
    }
  };

  const handleResumeSubscription = async () => {
    if (!user?.id) return;
    const success = await resumeSubscription(user.id);
    if (success) {
      const sub = await getSubscription(user.id);
      setSubscriptionPausedUntil(null);
      setSubscriptionExpiresAt(sub.expiresAt);
      feedback.showCreditToast('Subscription resumed! A new billing period has started.');
    } else {
      feedback.showErrorToast(t.toasts.resumeFailed);
    }
  };

  const handleLoadMore = async (): Promise<RankedPlace[]> => {
    if (!planning.searchData) return [];
    const effectiveVibes = planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes;
    try {
      const nextStage = Math.min(trips.findMoreStage + 1, 5) as FindMoreStage;
      const { places: morePlaces, reachedLimit, effectiveStage } = await loadMoreByStage(nextStage);
      trips.setFindMoreStage(effectiveStage ?? nextStage);
      if (reachedLimit) trips.setSearchLimitReached(true);
      if (morePlaces.length === 0) return [];
      const mode = resolveAiMode();
      const moreRanked = await rankPlaces(morePlaces, planning.searchData.location, planning.searchData.timeAvailable, planning.searchData.transport, effectiveVibes, language, mode, buildEnrichedTasteHint(effectiveVibes));
      await chargeHighCredit(moreRanked, mode);
      if (moreRanked.length > 0) {
        planning.setRankedPlaces(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          return [...prev, ...balanceByVibes(moreRanked.filter(p => !existingIds.has(p.id)), effectiveVibes)];
        });
      }
      return moreRanked;
    } catch (error) { console.error('Error loading more places:', error); return []; }
  };

  const handleOpenAuth = () => { feedback.setSettingsOpen(false); feedback.setAuthOpen(true); };
  const handleSignOut = async () => { await signOut(); feedback.setSettingsOpen(false); setAppMode('planning'); setSubscriptionExpiresAt(null); setSubscriptionPausedUntil(null); };

  const refreshSubscription = useCallback(() => {
    if (user?.id) {
      getSubscription(user.id).then((sub) => { setSubscriptionType(sub.type); setSubscriptionExpiresAt(sub.expiresAt); setSubscriptionPausedUntil(sub.pausedUntil); });
      loadCredits(user.id);
    }
  }, [user?.id, getSubscription, loadCredits]);

  // H-209: reconcile subscription & credits when tab regains focus
  // (catches mid-session purchases completed in another tab/window)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') refreshSubscription();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [refreshSubscription]);

  const openProTab = () => { setAppMode('pro'); refreshSubscription(); };

  const handleAuthSuccess = async () => {
    feedback.setAuthOpen(false);
    if (!safeStorage.getItem('tb_welcomed')) {
      safeStorage.setItem('tb_welcomed', '1');
      feedback.showToast('welcome');
    }
  };

  // Closing without signing in drops whatever was waiting on sign-in, unless an email is on its way.
  const handleAuthDismiss = (reason?: AuthDismissReason) => {
    feedback.setAuthOpen(false);
    pendingUpgradeRef.current = null;
    if (reason !== 'awaiting-email') {
      pendingSaveDataRef.current = null;
      clearPendingSave();
    }
  };

  const handleFindMorePlaces = async (itinerary: SavedItinerary): Promise<RankedPlace[]> => {
    trips.setLoadingMoreForTrip(true);
    try {
      const validation = await validateLocation(itinerary.location);
      const standardVibes = itinerary.vibes.filter(v => !v.startsWith('custom:'));
      const customVibe = itinerary.vibes.find(v => v.startsWith('custom:'))?.slice(7);
      const tripAiMode = (itinerary.ai_mode as AiMode) || 'medium';
      const mode: AiMode = tripAiMode === 'high' && !isPremium && (searchCredits?.deepSearchesRemaining ?? 0) <= 0 ? 'medium' : tripAiMode;
      const result = await searchNearby(itinerary.location, standardVibes, validation?.coords, itinerary.transport, itinerary.time_available, planning.distanceSettings, planning.categorySettings, !planning.showOpenOnly, planning.minRating, undefined, language, customVibe);
      if (result.places.length === 0) { trips.setLoadingMoreForTrip(false); return []; }
      const ranked = await rankPlaces(result.places, itinerary.location, itinerary.time_available, itinerary.transport, itinerary.vibes, language, mode, buildEnrichedTasteHint(itinerary.vibes));
      await chargeHighCredit(ranked, mode);
      trips.setLoadingMoreForTrip(false);
      return ranked;
    } catch (error) { console.error('Error finding more places:', error); trips.setLoadingMoreForTrip(false); return []; }
  };

  const handleOpenSavedTrips = () => {
    if (!isAuthenticated) { feedback.setAuthOpen(true); return; }
    trips.clearHighlight();
    if (appMode === 'savedTrips') trips.setSavedTripsResetCounter(c => c + 1);
    else setAppMode('savedTrips');
  };

  // ── Shared settings modal props ──
  const settingsProps = {
    aiMode: planning.aiMode,
    onAiModeChange: planning.setAiMode,
    distanceSettings: planning.distanceSettings,
    onDistanceChange: planning.setDistanceSettings,
    showOpenOnly: planning.showOpenOnly,
    onShowOpenOnlyChange: planning.setShowOpenOnly,
    minRating: planning.minRating,
    onMinRatingChange: planning.setMinRating,
    isAuthenticated,
    userEmail: user?.email,
    isAdmin,
    onOpenAuth: handleOpenAuth,
    onSignOut: handleSignOut,
    subscriptionType,
    onUpgradeToPremium: handleOpenPaymentFromSettings,
    onCancelPremium: handleCancelPremium,
    onPauseSubscription: handlePauseSubscription,
    onResumeSubscription: handleResumeSubscription,
    subscriptionPausedUntil,
    themeMode,
    onThemeModeChange: setThemeMode,
    userId: user?.id,
    tasteLikeCount: tasteWeights?.likeCount ?? 0,
    tasteDislikeCount: tasteWeights?.dislikeCount ?? 0,
    onOpenPro: openProTab,
    deepSearchesRemaining: searchCredits?.deepSearchesRemaining,
  };

  return (
    <>
      <SettingsModal isOpen={feedback.settingsOpen} onClose={() => feedback.setSettingsOpen(false)} {...settingsProps} subscriptionExpiresAt={subscriptionExpiresAt} referralCode={referralCode} />
      <AuthModal isOpen={feedback.authOpen} onClose={handleAuthDismiss} onSuccess={handleAuthSuccess} onSignIn={signIn} onSignUp={signUp} onGoogleSignIn={signInWithGoogle} onResetPassword={resetPassword} onEmailOtp={signInWithEmailOtp} onVerifyEmailOtp={verifyEmailOtp} />
      <PaymentModal isOpen={paymentState.isOpen} onClose={() => setPaymentState(prev => ({ ...prev, isOpen: false, pendingPlaces: null }))} onUpgrade={handleUpgrade} currentDayGroups={paymentState.dayGroups} currentLocationGroups={paymentState.locationGroups} maxFreeDayGroups={MAX_FREE_DAY_GROUPS} maxFreeLocationGroups={MAX_FREE_LOCATION_GROUPS} isBeta={isBetaMode} />

      {appMode === 'planning' && (
        <>
          {planning.planningView === 'input' && (
            <InputScreen
              onSubmit={handleSubmit}
              errorMessage={planning.errorMessage}
              onDismissError={() => planning.setErrorMessage(null)}
              previousData={planning.searchData || undefined}
              onOpenSettings={() => { feedback.setSettingsOpen(true); refreshSubscription(); }}
              onOpenSavedTrips={handleOpenSavedTrips}
              filteredClosedCount={planning.filteredClosedCount}
              isAuthenticated={isAuthenticated}
              hasSavedTrips={trips.groupedItineraries.length > 0}
              highlightMyTrips={trips.highlightMyTrips}
              isPremium={isPremium}
              savedInterestsActive={useSavedInterestsActive}
              hasSavedInterests={hasSavedInterests}
              onToggleSavedInterests={user?.id ? (val) => toggleUseInterests(user.id, val) : undefined}
              tasteDefaults={planning.tasteDefaults}
              onOpenPro={openProTab}
              referralCode={referralCode}
            />
          )}
          {planning.planningView === 'loading' && (
            <LoadingScreen
              stage={planning.loadingStage}
              locationName={planning.loadingLocationName}
              placesFound={planning.loadingPlacesFound}
              placesEliminated={planning.loadingPlacesEliminated}
              showTopupStage={planning.showTopupStage}
              firstPassAnalyzed={planning.firstPassAnalyzed}
              firstPassEliminated={planning.firstPassEliminated}
              rankingSubStage={planning.rankingSubStage}
              searchSubStage={planning.searchSubStage}
              searchProgress={planning.searchProgress}
              aiMode={planning.aiMode}
            />
          )}
          {planning.planningView === 'results' && planning.searchData && (
            <ResultsScreen
              places={planning.rankedPlaces}
              location={planning.searchData.location}
              timeAvailable={planning.searchData.timeAvailable}
              transport={planning.searchData.transport}
              vibes={planning.searchData.customVibe ? [...planning.searchData.vibes, `custom:${planning.searchData.customVibe}`] : planning.searchData.vibes}
              timezone={planning.searchData.timezone}
              onBack={handleBack}
              onSaveItinerary={handleSaveItinerary}
              onInstantSave={handleInstantSave}
              onInstantRemove={handleInstantRemove}
              onOpenSettings={() => { feedback.setSettingsOpen(true); refreshSubscription(); }}
              onLoadMore={handleLoadMore}
              findMoreStage={trips.findMoreStage}
              loadingMore={loadingMore}
              isAuthenticated={isAuthenticated}
              onOpenSavedTrips={handleOpenSavedTrips}
              onSaveSuccess={() => { trips.setSelectNewestTrip(false); setAppMode('savedTrips'); }}
              savedPlaceIds={trips.instantSavedPlaceIds}
              onSavedPlaceIdsChange={trips.setInstantSavedPlaceIds}
              referralCode={referralCode}
            />
          )}
        </>
      )}

      {appMode === 'savedTrips' && (
        <SavedTripsScreen
          groupedItineraries={trips.groupedItineraries}
          onBackToPlanning={() => { setAppMode('planning'); trips.setSelectNewestTrip(false); }}
          onDeleteItinerary={trips.handleDeleteItinerary}
          onDeleteGroup={trips.handleDeleteGroup}
          onDeletePlace={trips.handleDeletePlace}
          onFindMorePlaces={handleFindMorePlaces}
          onAddPlacesToTrip={trips.handleAddPlacesToTrip}
          onRenameTrip={trips.handleRenameTrip}
          onUnsaveNewPlace={async (itineraryId, placeId) => { const s = await trips.deleteSavedPlaceByPlaceId(itineraryId, placeId); if (s) await trips.loadSavedItineraries(); }}
          onShareTrip={trips.handleShareTrip}
          onUnshareTrip={trips.handleUnshareTrip}
          loading={isAuthenticated ? trips.itinerariesLoading : false}
          loadingMore={trips.loadingMoreForTrip}
          groupingType={tripGrouping}
          onGroupingChange={setTripGrouping}
          isPremium={isPremium}
          selectNewestOnOpen={trips.selectNewestTrip}
          resetToSummary={trips.savedTripsResetCounter}
          onHome={() => { setAppMode('planning'); planning.setPlanningView('input'); trips.setSelectNewestTrip(false); }}
          onUpgrade={handleOpenPaymentFromSettings}
          onMergeTrips={trips.handleMergeTrips}
          onSetPinned={trips.handleSetPinned}
          onUnpin={trips.handleUnpin}
          onReevaluationSaved={async () => { await trips.loadSavedItineraries(); triggerTripSaved(); }}
        />
      )}

      {appMode === 'settings' && (
        <SettingsModal isOpen={true} onClose={() => setAppMode('planning')} {...settingsProps} asPage pageMode="settings" />
      )}

      {appMode === 'filters' && (
        <SettingsModal
          isOpen={true}
          onClose={() => setAppMode('planning')}
          {...settingsProps}
          asPage
          pageMode="filters"
          isPremium={isPremium}
          savedInterests={savedInterests}
          onAddInterest={user?.id ? (vibe: string, interest: string) => addSavedInterest(user.id, vibe, interest) : undefined}
          onRemoveInterest={user?.id ? (vibe: string, interest: string) => removeSavedInterest(user.id, vibe, interest) : undefined}
          weather={planning.weather}
          weatherLoading={planning.weatherLoading}
          considerWeather={planning.considerWeather}
          onConsiderWeatherChange={planning.setConsiderWeather}
        />
      )}

      {appMode === 'pro' && (
        <ProScreen
          subscriptionType={subscriptionType}
          subscriptionExpiresAt={subscriptionExpiresAt}
          onUpgrade={handleUpgrade}
          onTip={async () => false}
          currentDayGroups={paymentState.dayGroups}
          currentLocationGroups={paymentState.locationGroups}
          maxFreeDayGroups={MAX_FREE_DAY_GROUPS}
          maxFreeLocationGroups={MAX_FREE_LOCATION_GROUPS}
          interestSearchesRemaining={searchCredits?.interestSearchesRemaining}
          deepSearchesRemaining={searchCredits?.deepSearchesRemaining}
          subscriptionPausedUntil={subscriptionPausedUntil}
          userId={user?.id}
          referralCode={referralCode}
          userName={user?.user_metadata?.full_name || user?.user_metadata?.name || null}
          isBeta={isBetaMode}
        />
      )}

      {!(appMode === 'planning' && planning.planningView === 'loading') && (
        <BottomTabBar
          activeTab={appMode === 'savedTrips' ? 'myTrips' : appMode === 'pro' ? 'pro' : appMode === 'settings' ? 'settings' : appMode === 'filters' ? 'filters' : 'explore'}
          onTabChange={(tab) => {
            if (tab === 'explore') { setAppMode('planning'); planning.setPlanningView('input'); trips.setSelectNewestTrip(false); }
            else if (tab === 'myTrips') { if (!isAuthenticated) { feedback.setAuthOpen(true); return; } handleOpenSavedTrips(); }
            else if (tab === 'filters') setAppMode('filters');
            else if (tab === 'settings') { setAppMode('settings'); refreshSubscription(); }
            else if (tab === 'pro') openProTab();
          }}
          showMyTrips={isAuthenticated && trips.groupedItineraries.length > 0}
        />
      )}

      {/* Toast layer */}
      {trips.tripSavedToast && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--accent-gold)]/30 shadow-lg text-sm text-[var(--text-primary)] animate-fade-in flex items-center gap-3">
          <span className="font-semibold">Trip saved!</span>
          <button onClick={() => { trips.setTripSavedToast(false); setAppMode('savedTrips'); trips.setSelectNewestTrip(true); }} className="text-xs text-[var(--accent-teal)] font-medium hover:underline">View & Share →</button>
        </div>
      )}
      {feedback.welcomeToast && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-xl bg-gradient-to-r from-[var(--accent-gold)] to-amber-500 text-black font-semibold text-sm shadow-lg shadow-amber-500/25 animate-fade-in">
          {t.toasts?.welcome || 'Welcome to TripBoom! Start exploring'} 🌍
        </div>
      )}
      {feedback.shareToast === 'first' && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-xl bg-gradient-to-r from-[var(--accent-teal)] to-emerald-500 text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 animate-fade-in">
          {t.toasts?.firstShare || 'Your first shared trip!'} 🎉
        </div>
      )}
      {feedback.creditToast && (
        <div className={`fixed left-1/2 -translate-x-1/2 z-[60] animate-fade-in max-w-sm ${trips.tripSavedToast || feedback.welcomeToast || feedback.shareToast ? 'top-[max(4rem,calc(env(safe-area-inset-top)+3.5rem))]' : 'top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))]'}`}>
          <div role={feedback.creditToastSeverity === 'error' ? 'alert' : 'status'} className={`relative px-5 py-3 rounded-xl text-white font-semibold text-sm shadow-lg text-center cursor-pointer ${feedback.creditToastSeverity === 'error' ? 'bg-gradient-to-r from-[var(--status-red)] to-red-600 shadow-red-500/25' : 'bg-gradient-to-r from-[var(--accent-teal)] to-teal-500 shadow-teal-500/25'}`} onClick={() => feedback.dismissToast('credit')}>
            {feedback.creditToast}
            {feedback.creditToastAction && (
              <button
                onClick={(e) => { e.stopPropagation(); const act = feedback.creditToastAction; feedback.dismissToast('credit'); act?.onClick(); }}
                className="ml-3 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold underline-offset-2 transition-colors"
              >
                {feedback.creditToastAction.label}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); feedback.dismissToast('credit'); }} className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/80 flex items-center justify-center text-black font-bold text-sm transition-colors">×</button>
          </div>
        </div>
      )}
      {feedback.referralBanner && !isAuthenticated && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] inset-x-0 mx-auto z-[60] animate-fade-in max-w-sm w-[calc(100%-2rem)]">
          <div className="relative px-4 py-3 rounded-xl bg-teal-600 dark:bg-teal-700 text-white text-sm shadow-lg shadow-teal-600/30 dark:shadow-teal-900/40">
            <p className="font-semibold">{t.referral?.friendShared || 'A friend shared TripBoom with you!'}</p>
            <p className="text-xs mt-0.5 text-teal-100">{t.referral?.signUpNudge || 'Sign in to unlock your free searches — and earn your friend bonus credits.'}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { feedback.setReferralBanner(false); feedback.setAuthOpen(true); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-teal-700 hover:bg-teal-50 transition-colors">{t.referral?.signUpFree || 'Sign in free'}</button>
              <button onClick={() => { feedback.setReferralBanner(false); safeStorage.setItem('tb_ref_dismissed', '1'); }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 hover:bg-white/25 text-white transition-colors">{t.referral?.maybeLater || 'Maybe later'}</button>
            </div>
          </div>
        </div>
      )}
      {feedback.expiredToast && (
        <div className="fixed top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] inset-x-0 mx-auto z-[60] animate-fade-in max-w-sm w-[calc(100%-2rem)]">
          <div className="relative px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-[var(--accent-gold)] text-black text-sm shadow-lg shadow-amber-500/30">
            <p className="font-semibold">{t.toasts?.subscriptionExpiredTitle || 'Your subscription has ended'}</p>
            <p className="text-xs mt-0.5 text-amber-900/70">{t.toasts?.subscriptionExpiredBody || 'Subscribe again to unlock unlimited searches, saves, and AI features.'}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { feedback.dismissToast('expired'); openProTab(); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-black/80 text-white hover:bg-black transition-colors">{t.toasts?.subscriptionExpiredCTA || 'View plans'}</button>
              <button onClick={() => feedback.dismissToast('expired')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/30 hover:bg-white/50 text-black transition-colors">{t.toasts?.dismiss || 'Dismiss'}</button>
            </div>
          </div>
        </div>
      )}
      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App;
